import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, ChevronRight, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "./Bits";
import { scrollToSupport } from "./content";

const MONTH = () => new Date().toLocaleDateString("de-DE", { month: "long" });

export function useSupporterGoal() {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/supporters/goal").then(({ data }) => setG(data)).catch(() => {}); }, []);
  if (!g?.goal) return null;
  return { ...g, pct: Math.min(100, Math.round((g.count / g.goal) * 100)), done: g.count >= g.goal, left: Math.max(0, g.goal - g.count), month: MONTH() };
}

export function GlowBar({ pct, testId, className = "h-3.5" }) {
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(pct), 120); return () => clearTimeout(t); }, [pct]);
  return (
    <div className={`relative rounded-full bg-rose-100/80 ring-1 ring-pink-200/70 p-0.5 ${className}`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} data-testid={testId}>
      <div className="goal-glow-fill relative h-full rounded-full pink-gradient overflow-hidden transition-[width] duration-[1400ms] ease-out" style={{ width: `${Math.max(w, pct ? 4 : 0)}%` }}>
        <span className="goal-glow-sheen absolute inset-0" aria-hidden />
      </div>
    </div>
  );
}

const Info = ({ g }) => (g.done
  ? <><PartyPopper className="w-4 h-4 inline -mt-0.5" /> Ziel erreicht – danke euch allen! ❤️</>
  : <>Noch <b className="text-rose-600">{g.left}</b> {g.left === 1 ? "neuer Projekt Unterstützer" : "neue Projekt Unterstützer"} bis zum Monatsziel</>);

export function MonthGoal() {
  const g = useSupporterGoal();
  if (!g) return null;
  return (
    <section data-testid="cs-goal" aria-labelledby="cs-goal-title">
      <Reveal className="relative overflow-hidden rounded-[2rem] bg-white/90 backdrop-blur-xl border border-pink-100 p-6 sm:p-8 shadow-[0_24px_60px_-34px_rgba(225,29,99,.55)]">
        <span className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-rose-100/70 blur-3xl" aria-hidden />
        <div className="relative flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-rose-500 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Unser Ziel im {g.month}</p>
            <h2 id="cs-goal-title" className="font-display text-xl sm:text-2xl font-black tracking-tight text-slate-900 mt-1.5">🎯 {g.goal} neue Projekt Unterstützer</h2>
          </div>
          <p className="font-display text-3xl sm:text-4xl font-black text-rose-600 tabular-nums" data-testid="cs-goal-count">{g.count}<span className="text-slate-300"> / </span>{g.goal}</p>
        </div>
        <GlowBar pct={g.pct} testId="cs-goal-bar" className="relative mt-5 h-4" />
        <div className="relative mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600" data-testid="cs-goal-info"><Info g={g} /></p>
          {!g.done && <button onClick={scrollToSupport} data-testid="cs-goal-cta" className="h-10 px-4 rounded-full pink-gradient text-white text-sm font-extrabold shadow-md shadow-rose-300/50 hover:-translate-y-0.5 active:scale-95 transition-transform">❤️ Mithelfen</button>}
        </div>
      </Reveal>
    </section>
  );
}

export function MonthGoalFeedCard() {
  const g = useSupporterGoal();
  if (!g) return null;
  return (
    <Link to="/community-unterstuetzen" data-testid="feed-month-goal"
      className="group block rounded-3xl bg-white border border-pink-100 p-4 shadow-[0_14px_34px_-24px_rgba(157,20,70,.5)] hover:-translate-y-0.5 transition-transform">
      <span className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 min-w-0 font-bold text-slate-900"><span className="w-8 h-8 shrink-0 rounded-xl pink-gradient text-white flex items-center justify-center"><Target className="w-4 h-4" /></span> <span className="truncate">Ziel im {g.month}</span></span>
        <span className="shrink-0 flex items-center gap-1 text-sm font-black text-rose-600 tabular-nums" data-testid="feed-month-goal-count">{g.count}/{g.goal}<ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
      </span>
      <span className="mt-0.5 block text-[11px] font-semibold text-slate-500 pl-10">Neue Projekt Unterstützer in diesem Monat</span>
      <GlowBar pct={g.pct} testId="feed-month-goal-bar" className="mt-3 h-3" />
      <span className="mt-2 block text-xs text-slate-600"><Info g={g} /></span>
    </Link>
  );
}
